import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {capitalize, cloneDeep} from "lodash";
import {OptionLabelValue} from "../../../types/internal/OptionLabelValue";
import {parseOptions} from "../../../redux/api/assets";
import NEWListWithSearchFilter from "../../common/filters/NEWListWithSearchFilter";
import {fromFeToBeImportReportTableFilters, ImportReportTableColumns,} from "../../../types/internal/ImportReportTable";
import {useLazyGetImportReportRelatedOptionsForInfiniteScrollingQuery} from "../../../redux/api/importReport";
import {getTranslationImportReportTableColumnKey} from "../../../utils/translation";
import {REPORT_DETAIL_STATUS} from "../../../utils/dataImport";

export default function ImportReportRelatedOptionsFilter({field, reportUuid, filters, setFilters, status}: {
  reportUuid: string,
  field: string,
  filters: Record<string, OptionLabelValue[]>,
  setFilters: (filters: Record<string, OptionLabelValue[]>) => void,
  status?: REPORT_DETAIL_STATUS
}) {

  const [getData, {
    data,
    isLoading,
    isFetching,
    isUninitialized,
    originalArgs,
  }] = useLazyGetImportReportRelatedOptionsForInfiniteScrollingQuery()
  const [querySearch, setQuerySearch] = useState<string>('');
  const options: OptionLabelValue[] = useMemo(() => parseOptions(data?.results || []), [data?.results]);
  const total = data?.count || 0;
  const {t} = useTranslation();
  const placeholder = capitalize(t('table.filterPlaceholder', {column: t(getTranslationImportReportTableColumnKey(field as ImportReportTableColumns))}));
  const selectedFilters = filters[field] || [];

  const {[field]: currentFilter, ...importReportFiltersWithoutCurrentFilter} = filters
  const importReportTableFiltersWithoutCurrentFilterRef = useRef<{[key: string]: OptionLabelValue[]}>(importReportFiltersWithoutCurrentFilter)

  useEffect(() => {
    const {[field]: currentFilter, ...importReportFiltersWithoutCurrentFilter} = filters
    importReportTableFiltersWithoutCurrentFilterRef.current = importReportFiltersWithoutCurrentFilter
  }, [filters, field]);

  const handleGetNext = useCallback(() => {
    getData({
      column: field as keyof ImportReportTableColumns,
      ...!!querySearch && {column_search: querySearch},
      ...fromFeToBeImportReportTableFilters(importReportTableFiltersWithoutCurrentFilterRef.current),
      status,
      page: (originalArgs?.page || 0) + 1,
      page_size: 15,
      report_uuid: reportUuid
    });
  }, [reportUuid, field, getData, originalArgs?.page, querySearch, status]);

  useEffect(() => {
    getData({
      column: field as keyof ImportReportTableColumns,
      ...!!querySearch && {column_search: querySearch},
      ...fromFeToBeImportReportTableFilters(importReportTableFiltersWithoutCurrentFilterRef.current),
      status,
      page: 1,
      page_size: 15,
      report_uuid: reportUuid
    });
  }, [reportUuid, field, getData, querySearch, status]);

  return <>
    <NEWListWithSearchFilter
      getNext={handleGetNext}
      options={options}
      selectedOptions={selectedFilters}
      setSelectedOptions={values => {
        if (values.length) {
          setFilters({...filters, [field]: values});
        } else {
          const newFilters = cloneDeep(filters);
          delete newFilters[field];
          setFilters(newFilters);
        }
      }}
      total={total}
      isLoadingOptions={isLoading || isFetching || isUninitialized}
      querySearch={querySearch}
      setQuerySearch={setQuerySearch}
      search={{placeholder}}
    />
  </>;
}